import { Box, RBox, Cyl, Segment, BarSegment } from './helpers';
import type { MatsProps } from './materials';

const SIDES = [1, -1] as const;

/** Windshield + reflective glass, cab side windows, A-pillars, mirrors, headlight, bumper, plate. */
export function Cab({ mats }: MatsProps) {
  return (
    <group>
      {/* Windshield assembly (tilted back) */}
      <group position={[0.98, 1.18, 0]} rotation={[0, 0, 0.3]}>
        <Box args={[0.02, 0.46, 0.94]} mat={mats.cabinDark} pos={[-0.05, 0, 0]} />
        <Box args={[0.02, 0.4, 0.86]} mat={mats.glass} pos={[-0.02, 0, 0]} />
        <RBox args={[0.05, 0.05, 0.98]} radius={0.02} smoothness={3} mat={mats.paint} pos={[0, 0.24, 0]} />
        <RBox args={[0.05, 0.05, 0.98]} radius={0.02} smoothness={3} mat={mats.paint} pos={[0, -0.24, 0]} />
        <RBox args={[0.05, 0.5, 0.05]} radius={0.02} smoothness={3} mat={mats.paint} pos={[0, 0, 0.47]} />
        <RBox args={[0.05, 0.5, 0.05]} radius={0.02} smoothness={3} mat={mats.paint} pos={[0, 0, -0.47]} />
        <Box args={[0.03, 0.44, 0.9]} mat={mats.gasket} pos={[0, 0, 0]} />
        <Box args={[0.026, 0.44, 0.02]} mat={mats.paint} pos={[0.005, 0, 0]} />
        {/* reflection streak */}
        <mesh position={[0.02, 0.03, 0]} rotation={[0, Math.PI / 2, 0.42]}>
          <planeGeometry args={[0.62, 0.09]} />
          <meshBasicMaterial color="#dff0f7" transparent opacity={0.3} depthWrite={false} toneMapped={false} />
        </mesh>
      </group>

      {/* Wipers */}
      {[0.18, -0.18].map((z) => (
        <Cyl key={z} args={[0.008, 0.008, 0.34, 8]} mat={mats.black} pos={[1.02, 1.06, z]} rot={[0, 0, 0.9]} />
      ))}

      {/* Door windows (both sides) */}
      {SIDES.map((s) => {
        const zc = 0.612 * s;
        return (
          <group key={s}>
            <Box args={[0.44, 0.29, 0.012]} mat={mats.glassDoor} pos={[0.56, 1.18, zc + 0.004 * s]} />
            <RBox args={[0.5, 0.04, 0.035]} radius={0.012} smoothness={3} mat={mats.paint} pos={[0.56, 1.355, zc + 0.01 * s]} />
            <RBox args={[0.5, 0.04, 0.035]} radius={0.012} smoothness={3} mat={mats.paint} pos={[0.56, 1.005, zc + 0.01 * s]} />
            <RBox args={[0.04, 0.35, 0.035]} radius={0.012} smoothness={3} mat={mats.paint} pos={[0.81, 1.18, zc + 0.01 * s]} />
            <RBox args={[0.04, 0.35, 0.035]} radius={0.012} smoothness={3} mat={mats.paint} pos={[0.31, 1.18, zc + 0.01 * s]} />
            {/* door handle */}
            <RBox args={[0.07, 0.028, 0.03]} radius={0.01} smoothness={3} mat={mats.chromeHi} pos={[0.78, 1.02, 0.615 * s + 0.012 * s]} />
          </group>
        );
      })}

      {/* A-pillars */}
      {SIDES.map((s) => (
        <BarSegment key={s} a={[1.055, 0.98, s * 0.505]} b={[0.905, 1.44, s * 0.505]} w={0.05} d={0.055} mat={mats.paint} />
      ))}

      {/* Headlight + bumper + plate */}
      <Cyl args={[0.1, 0.08, 0.09, 24]} mat={mats.chromeHi} pos={[1.5, 0.62, 0]} rot={[0, 0, Math.PI / 2]} />
      <mesh position={[1.548, 0.62, 0]} rotation={[0, Math.PI / 2, 0]} material={mats.headlight}>
        <circleGeometry args={[0.075, 24]} />
      </mesh>
      <Cyl args={[0.024, 0.024, 0.86, 12]} mat={mats.chrome} pos={[1.48, 0.42, 0]} rot={[Math.PI / 2, 0, 0]} />
      <RBox args={[0.26, 0.1, 0.02]} radius={0.01} smoothness={3} mat={mats.plate} pos={[1.56, 0.4, 0]} rot={[0, Math.PI / 2, 0]} />

      {/* Wing mirrors */}
      {SIDES.map((s) => {
        const zc = 0.72 * s;
        return (
          <group key={s}>
            <RBox args={[0.145, 0.1, 0.028]} radius={0.022} smoothness={3} mat={mats.black} pos={[0.98, 1.22, zc]} rot={[0, -0.16 * s, 0]} />
            <mesh position={[0.98, 1.22, zc + 0.016 * s]} rotation={[0, (s > 0 ? 0 : Math.PI) - 0.16 * s, 0]} material={mats.mirrorGlass}>
              <planeGeometry args={[0.116, 0.074]} />
            </mesh>
            <Segment a={[0.91, 1.17, 0.586 * s]} b={[0.96, 1.2, 0.665 * s]} r={0.009} mat={mats.black} />
            <Segment a={[0.96, 1.2, 0.665 * s]} b={[0.97, 1.2, 0.704 * s]} r={0.009} mat={mats.black} />
          </group>
        );
      })}
    </group>
  );
}
